import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ReactComponent as ErrorWarning } from "../../assets/images/error-warning.svg";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { selectFormValidityByKey } from "../../redux/slices/formValiditySlice/formValiditySelector";
import { MuiBox } from "../box/MuiBox";
import { AccordionHeaderWrapper } from "./AccordionHeader.style";

interface ExpandableProps {
  title: React.ReactNode;
  children: React.ReactNode;
  onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
  isExpanded?: boolean;
  showExpandCollapseIcon?: boolean;
  className?: string;
  wrapperClassName?: string;
  formKey?: string;
  isFormArrayError?: boolean;
}

export const AccordionHeader: React.FC<ExpandableProps> = ({
  title,
  children,
  onChange,
  isExpanded,
  showExpandCollapseIcon: isAccordion = true,
  className,
  wrapperClassName,
  formKey,
  isFormArrayError,
}) => {
  const { isMobile } = useMediaQueries();
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (isExpanded !== undefined) {
      setExpanded(isExpanded);
    }
  }, [isExpanded]);

  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
    onChange?.(event, isExpanded);
  };

  const { commonARIALabel } = useNavigationSiteCore();
  const isValid =
    useSelector(selectFormValidityByKey(formKey as string)) ?? true;
  const showErrorIcon = isValid === false;

  return (
    <AccordionHeaderWrapper className={wrapperClassName}>
      {isAccordion ? (
        <Accordion
          expanded={expanded}
          onChange={handleChange}
          className={className}
          slotProps={{ heading: { component: "label" } }}
        >
          <AccordionSummary
            expandIcon={
              expanded ? (
                <RemoveIcon
                  data-testid={COMPONENT_IDS.COMMON_ACCORDION_REMOVE_ICON}
                  color="primary"
                  aria-label={commonARIALabel.collapseAccordion}
                />
              ) : (
                <AddIcon
                  data-testid={COMPONENT_IDS.COMMON_ACCORDION_ADD_ICON}
                  color="primary"
                  aria-label={commonARIALabel.expandAccordion}
                />
              )
            }
            data-testid={COMPONENT_IDS.COMMON_ACCORDION_INDIVIDUAL}
          >
            {isMobile && (isFormArrayError ?? showErrorIcon) && (
              <MuiBox className="accordion-error-icon">
                <ErrorWarning />
              </MuiBox>
            )}
            <MuiBox className="accordion-title">{title}</MuiBox>
          </AccordionSummary>
          <AccordionDetails>{children}</AccordionDetails>
        </Accordion>
      ) : (
        <MuiBox className="container-no-accordion">
          <MuiBox className="no-accordion-title">{title}</MuiBox>
          <MuiBox>{children}</MuiBox>
        </MuiBox>
      )}
    </AccordionHeaderWrapper>
  );
};
